import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import { List, Drawer, Tooltip, notification } from 'antd';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import '../data.scss';
import { getUser, getAclMenu, getToken, fetchWithAuth } from '../utils/common';
import logger from '../utils/logger';
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  TableHead,
  Switch,
  Grid,
  Avatar,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { TreeView } from '@mui/x-tree-view/TreeView';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import 'react-dropdown-tree-select/dist/styles.css';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { UserListToolbar } from '../sections/@dashboard/user';
import { SmartshileService } from './dataset/service';

function checkAncestors(data, nodeId, status) {
  const updated = [...data];
  let node = updated.find((item) => item.id === nodeId);

  while (node?.parent_id) {
    const parentIndex = updated.findIndex((item) => item.id === node.parent_id);
    if (parentIndex >= 0) {
      updated[parentIndex] = { ...updated[parentIndex], check: status };
      node = updated[parentIndex];
    } else {
      break;
    }
  }

  return updated;
}

function getDescendantIds(nodeId, items) {
  const children = items.filter((item) => item.parent_id === nodeId);
  let descendants = children.map((child, _i) => child.id);

  for (const child of children) {
    descendants = descendants.concat(getDescendantIds(child.id, items));
  }

  return descendants;
}

function filterDataAfterUncheck(uncheckedId, data) {
  const descendants = getDescendantIds(uncheckedId, data);
  const idsToRemove = new Set();

  for (const item of data) {
    if (item.id === uncheckedId || descendants.includes(item.id)) {
      if (item.check) {
        idsToRemove.add(item.id);
      }
    }
  }

  return data.filter((item) => !idsToRemove.has(item.id));
}

// Dialog Components
function DialogChangeRole({
  isConfirmationModalOpen,
  handleCloseConfirmationModal,
  statelabel,
  t,
  handleCancel,
  isLoading,
  handleConfirm,
}) {
  return (
    <Dialog open={isConfirmationModalOpen} onClose={handleCloseConfirmationModal}>
      <DialogTitle>{t('Confirmation.1')}</DialogTitle>
      <DialogContent>{statelabel.label}</DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>{t('CancelButton.1')}</Button>
        <LoadingButton
          disabled={isLoading}
          style={{ backgroundColor: isLoading ? 'gray' : '#12A2CE', color: 'white' }}
          onClick={handleConfirm}
        >
          {isLoading ? 'Loading...' : t('Confirm.1')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

DialogChangeRole.propTypes = {
  isConfirmationModalOpen: PropTypes.bool,
  handleCloseConfirmationModal: PropTypes.func,
  statelabel: PropTypes.object,
  t: PropTypes.func,
  handleCancel: PropTypes.func,
  isLoading: PropTypes.bool,
  handleConfirm: PropTypes.func,
};

function DialogChangeRoleWithDataset({
  isDatasetModalOpen,
  setDatasetModalOpen,
  statelabel,
  t,
  filter,
  handleFilterChange,
  parent,
  renderTree,
  isLoading,
  handleConfirm,
}) {
  return (
    <Dialog fullWidth maxWidth="sm" open={isDatasetModalOpen} onClose={() => setDatasetModalOpen(false)}>
      <DialogTitle>{t('Confirmation.1')}</DialogTitle>
      <DialogContent>
        <Typography>{statelabel.label}</Typography>
        <Divider sx={{ my: 2 }} />
        <Grid item xs={12} container direction={'row'}>
          <Grid item xs={12}>
            <TextField
              label="Set Data Record Viewing Permission"
              variant="outlined"
              value={filter}
              size="small"
              onChange={handleFilterChange}
              style={{ marginBottom: '20px', width: '100%' }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sx={{ maxHeight: '40vh', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}
          >
            <TreeView defaultCollapseIcon={<ExpandMoreIcon />} defaultExpandIcon={<ChevronRightIcon />}>
              {Array.isArray(parent) && parent.length > 0
                ? parent.map((node, _i) => renderTree(node, true, node.name))
                : null}
            </TreeView>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDatasetModalOpen(false)}>{t('CancelButton.1')}</Button>
        <LoadingButton
          disabled={isLoading}
          style={{ backgroundColor: isLoading ? 'gray' : '#12A2CE', color: 'white' }}
          onClick={handleConfirm}
        >
          {isLoading ? 'Loading...' : t('Confirm.1')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

DialogChangeRoleWithDataset.propTypes = {
  isDatasetModalOpen: PropTypes.bool,
  setDatasetModalOpen: PropTypes.func,
  statelabel: PropTypes.object,
  t: PropTypes.func,
  filter: PropTypes.string,
  handleFilterChange: PropTypes.func,
  parent: PropTypes.array,
  renderTree: PropTypes.func,
  isLoading: PropTypes.bool,
  handleConfirm: PropTypes.func,
};

function DialogSwitch({ isSwitchOpen, handleCloseSwitchnModal, t, stateSwitching, isLoading, handleConfirmSwitch }) {
  return (
    <Dialog open={isSwitchOpen} onClose={handleCloseSwitchnModal}>
      <DialogTitle>{t('Confirmation.1')}</DialogTitle>
      <DialogContent>
        {t('AreYuSureSwitch.1')}{' '}
        <span style={{ fontWeight: 'bold' }}>
          {stateSwitching.statusBefore ? t('Active.1') : t('StatusInactive.1')}
        </span>{' '}
        {t('AreYuSureSwitch.2')}{' '}
        <span style={{ fontWeight: 'bold' }}>{stateSwitching.statusAfter ? t('Active.1') : t('StatusInactive.1')}</span>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseSwitchnModal}>{t('CancelButton.1')}</Button>
        <LoadingButton
          disabled={isLoading}
          style={{ backgroundColor: isLoading ? 'gray' : '#12A2CE', color: 'white' }}
          onClick={handleConfirmSwitch}
        >
          {isLoading ? 'Loading...' : t('Confirm.1')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

DialogSwitch.propTypes = {
  isSwitchOpen: PropTypes.bool,
  handleCloseSwitchnModal: PropTypes.func,
  t: PropTypes.func,
  stateSwitching: PropTypes.object,
  isLoading: PropTypes.bool,
  handleConfirmSwitch: PropTypes.func,
};

function DialogReset({ isSwitchOpender, handleCloseSwitchnModalder, t, isLoading, handleConfirmReset }) {
  return (
    <Dialog open={isSwitchOpender} onClose={handleCloseSwitchnModalder}>
      <DialogTitle>{t('Confirmation.1')}</DialogTitle>
      <DialogContent>{t('ResetPrompt.1')}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseSwitchnModalder}>{t('CancelButton.1')}</Button>
        <LoadingButton
          disabled={isLoading}
          style={{ backgroundColor: isLoading ? 'gray' : '#12A2CE', color: 'white' }}
          onClick={handleConfirmReset}
        >
          {isLoading ? 'Loading...' : t('Confirm.1')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

DialogReset.propTypes = {
  isSwitchOpender: PropTypes.bool,
  handleCloseSwitchnModalder: PropTypes.func,
  t: PropTypes.func,
  isLoading: PropTypes.bool,
  handleConfirmReset: PropTypes.func,
};

// Helper function to get action prompt text
function getActionPromptText(index, t) {
  if (index?.attributes?.status[0] !== 'Waiting Approval' || !index?.attributes?.action?.[0]) {
    return t('AddUserPrompt.1');
  }

  const action = index?.attributes?.action?.[0];
  let actionText = '';

  switch (action) {
    case 'reset':
      actionText = t('ResetPrompt.1');
      break;
    case 'delete':
      actionText = t('DeletePrompt.1');
      break;
    case 'update dataset access':
      actionText = t('UpdateDatasetAccess.1');
      break;
    default:
      actionText = t('ChangeRolePrompt.1');
      break;
  }

  return `${t('AreYuSur.1')} ${actionText}`;
}

// Helper function to get action prompt text for approve actions
function getApproveActionPromptText(index, t) {
  if (index?.attributes?.status[0] !== 'Waiting Approval' || !index?.attributes?.action?.[0]) {
    return t('AddUserPrompt.1');
  }

  const action = index?.attributes?.action?.[0];
  let actionText = '';

  switch (action) {
    case 'reset':
      actionText = 'reseting password';
      break;
    case 'delete':
      actionText = 'deleting';
      break;
    case 'update dataset access':
      actionText = 'updating dataset access';
      break;
    default:
      actionText = action;
      break;
  }

  return `${t('AreYuSureApprove.1')} ${actionText} ${t('AreYuSureApprove.7')}`;
}

// Helper function to get action prompt text for reject actions
function getRejectActionPromptText(index, t) {
  if (index?.attributes?.status[0] !== 'Waiting Approval' || !index?.attributes?.action?.[0]) {
    return t('AddUserPrompt.1');
  }

  const action = index?.attributes?.action?.[0];
  let actionText = '';

  switch (action) {
    case 'reset':
      actionText = 'reseting password';
      break;
    case 'delete':
      actionText = 'deleting';
      break;
    case 'update dataset access':
      actionText = 'updating dataset access';
      break;
    default:
      actionText = action;
      break;
  }

  return `Are you sure you want to reject ${actionText} for this user?`;
}

// Helper function to get status background color
function getStatusBackgroundColor(index) {
  // Check if user has custom status attributes (for pending approvals, etc.)
  if (index?.attributes?.status?.[0] === 'InActive') {
    return 'red';
  }
  if (index?.attributes?.status?.[0] === 'Waiting Approval') {
    return '#8766EC';
  }
  if (index?.attributes?.status?.[0] === 'Waiting Activation' || index?.attributes?.status?.[0] === 'Waiting Reset') {
    return '#FFCE4A';
  }

  // Default status based on enabled property
  if (!index.enabled) {
    return 'red';
  }

  // Active users (enabled = true)
  return '#12A2CE';
}

// Helper function to get status text
function getStatusText(index, t) {
  // Check if user has custom status attributes (for pending approvals, etc.)
  if (index?.attributes?.status?.[0] === 'InActive') {
    return t('StatusInactive.1');
  }
  if (index?.attributes?.status?.[0] === 'Waiting Approval') {
    return t('StatusWaitApproval.1');
  }
  if (index?.attributes?.status?.[0] === 'Waiting Reset') {
    return t('StatusWaitReset.1');
  }
  if (index?.attributes?.status?.[0] === 'Waiting Activation') {
    return t('StatusWaitActivation.1');
  }

  // Default status based on enabled property
  if (!index.enabled) {
    return t('StatusInactive.1');
  }

  // Active users (enabled = true)
  return t('Active.1');
}

// Helper function to get action title
function getActionTitle(index) {
  const action = index?.attributes?.action?.[0];
  const targetRole = index?.attributes?.target_role?.[0];

  if (action === 'delete') {
    return 'Delete User';
  }
  if (action === 'reset') {
    return 'Reset User Password';
  }
  if (action === 'update dataset access') {
    return 'Update Dataset Access';
  }
  if (targetRole) {
    return 'Change Role';
  }
  return 'Add User';
}

// Helper functions moved to outer scope for better performance
function checkAncestorsIfChildrenChecked(data) {
  const mapById = Object.fromEntries(data.map((item, _i) => [item.id, { ...item }]));
  const childrenMap = {};
  for (const item of data) {
    if (!childrenMap[item.parent_id]) childrenMap[item.parent_id] = [];
    childrenMap[item.parent_id].push(item.id);
  }

  const areAllChildrenChecked = (id) => {
    const children = childrenMap[id] || [];
    return children.every((childId) => mapById[childId].check);
  };

  const propagateCheck = (id) => {
    if (!id || !mapById[id]) return;
    if (areAllChildrenChecked(id)) {
      mapById[id].check = true;
      const parentId = mapById[id].parent_id;
      propagateCheck(parentId);
    }
  };

  for (const item of Object.values(mapById)) {
    if (item.check) {
      propagateCheck(item.parent_id);
    }
  }

  return Object.values(mapById);
}

function removeCheckByIds(data, targetIds) {
  return data.map((item, _i) => {
    if (targetIds.includes(item.id)) {
      const { check, ...rest } = item;
      return rest;
    }
    return item;
  });
}

export default function UserPage() {
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);
  const [selected] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [data, setData] = useState({ data: [], total: 0 });
  const [data2, setData2] = useState([]);
  const [filter, setFilter] = useState('');

  // Debounce mechanism to prevent race conditions during navigation
  const apiCallTimeoutRef = useRef(null);
  const debouncedApiCall = useCallback((apiFunction, delay = 300) => {
    return (...args) => {
      if (apiCallTimeoutRef.current) {
        clearTimeout(apiCallTimeoutRef.current);
      }
      apiCallTimeoutRef.current = setTimeout(() => {
        apiFunction(...args);
      }, delay);
    };
  }, []);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isDatasetModalOpen, setDatasetModalOpen] = useState(false);
  const [isSwitchOpen, setisSwitchOpen] = useState(false);
  const [isSwitchOpender, setisSwitchOpender] = useState(false);
  const [allData, setAllData] = useState();
  const [statelabel, setStatelabel] = useState({ from: '', to: '', id: '', status: '', label: '', isactive: false });
  const [invitation, setInvitation] = useState({ id: '', email: '', role: '', errorText: '' });
  const [resetpass, setResetpass] = useState();
  const [datas, setDatas] = useState([]);
  const [idAlreadyFetxh, setIdAlreadyFetch] = useState([]);
  const [parent, setParent] = useState([]);
  const { getDataSet } = SmartshileService();

  const [checkedItems, setCheckedItems] = useState([]);

  // Get ACL permissions and user info
  const acl = getAclMenu('user') || {};
  const userData = getUser();
  const username = (typeof userData === 'string' ? userData : userData?.email) || '';

  // Helper function to expand checked items to include all children
  const expandCheckedItems = (checkedItems, parentData) => {
    const expandedItems = [...checkedItems];

    // For each checked item, find it in the parent data and add all its children
    checkedItems.forEach((checkedItem) => {
      if (checkedItem.type === 'folder' && checkedItem.check) {
        const parentNode = findNodeById(parentData, checkedItem.id);
        if (parentNode && parentNode.children) {
          // Add all children of this checked parent
          parentNode.children.forEach((child) => {
            // Only add if not already in the list
            if (!expandedItems.some((item) => item.id === child.id)) {
              expandedItems.push({
                id: child.id,
                type: child.type,
                parent_id: child.idparrent || '',
                check: true,
              });
            }
          });
        }
      }
    });

    return expandedItems;
  };

  // Helper function to check children nodes
  const checkChildren = (node, checked, newCheckedItems) => {
    if (checked) {
      newCheckedItems.push({
        id: node.id,
        type: node.type,
        parent_id: node.idparrent || '',
        check: true,
      });
    } else {
      const filteredItems = filterDataAfterUncheck(node.id, newCheckedItems);
      const index = filteredItems.findIndex((item) => item.id === node.id);
      if (index > -1) {
        filteredItems.splice(index, 1);
      }
      newCheckedItems.length = 0;
      newCheckedItems.push(...filteredItems);
    }
    for (const child of node.children || []) {
      checkChildren(child, checked, newCheckedItems);
    }
  };

  // Helper function to update check status by ID
  const updateCheckStatusById = (items, targetId, newCheckValue) => {
    return items.map((item, _i) => (item.id === targetId ? { ...item, check: newCheckValue } : item));
  };

  // Helper function to check parent nodes
  const checkParent = (node, newCheckedItems) => {
    if (!node.idparrent) return;

    const parentIndex = newCheckedItems.findIndex((item) => item.id === node.idparrent);
    const parentNode = findNodeById(parent, node.idparrent);

    if (parentNode) {
      const allChildrenChecked = parentNode.children.every((child) =>
        newCheckedItems?.some((item) => item.id === child.id && item.check)
      );
      const someChildrenChecked = parentNode.children?.some((child) =>
        newCheckedItems?.some((item) => item.id === child.id)
      );

      if (allChildrenChecked) {
        newCheckedItems = checkAncestors(newCheckedItems, parentNode.id, true);
        newCheckedItems = checkAncestorsIfChildrenChecked(newCheckedItems);

        if (parentIndex === -1) {
          newCheckedItems.push({
            id: parentNode.id,
            type: parentNode.type,
            parent_id: parentNode.idparrent || '',
            check: true,
          });
        } else {
          newCheckedItems[parentIndex] = {
            ...newCheckedItems[parentIndex],
            check: true,
          };
        }
      } else if (someChildrenChecked) {
        newCheckedItems.push({
          id: parentNode.id,
          type: parentNode.type,
          parent_id: parentNode.idparrent || '',
          check: false,
        });
      } else if (!someChildrenChecked && parentIndex > -1) {
        newCheckedItems.splice(parentIndex, 1);
      }
      checkParent(parentNode, newCheckedItems);
    }
  };

  const handleCheck = (event, node) => {
    const newCheckedItems = checkedItems != null ? [...checkedItems] : [];

    checkChildren(node, event.target.checked, newCheckedItems);
    checkParent(node, newCheckedItems);

    // Filter out malformed items with undefined id
    const validCheckedItems = newCheckedItems.filter((item) => item.id !== undefined);

    const uniqueCheckedItems = [...new Map(validCheckedItems.map((item, _i) => [item.id, item])).values()];
    setCheckedItems(uniqueCheckedItems);
  };

  const handleCheck2 = (node, checked, parentId) => {
    const newCheckedItems = checkedItems != null ? [...checkedItems] : [];

    const checkChildren = (node, checked) => {
      if (checked) {
        newCheckedItems.push({
          id: node.id,
          type: node.type,
          parent_id: String(node?.data?.parentId || parentId || ''),
          check: true,
        });
      } else {
        const index = newCheckedItems.findIndex((item) => item.id === node.id);
        if (index > -1) {
          newCheckedItems.splice(index, 1);
        }
      }
      for (const child of node.children || []) {
        checkChildren(child, checked);
      }
    };

    node.map((node2, _i) => {
      checkChildren(node2, checked);
    });
    checkChildren(node, true);

    const uniqueCheckedItems = [...new Map(newCheckedItems.map((item, _i) => [item.id, item])).values()];
    setCheckedItems(uniqueCheckedItems);
  };

  const findNodeById = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.children) {
        const result = findNodeById(node.children, id);
        if (result) return result;
      }
    }
    return null;
  };

  const [stateSwitching, setStateSwitching] = useState({ statusBefore: '', statusAfter: '', id: '' });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${newPage * rowsPerPage}`);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${Number.parseInt(event.target.value, 10)}&first=${0}`);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    fetchInfo(`${globalThis.REACT_APP_API_PURE}user?search=${event.target.value}&max=${rowsPerPage}&first=0`);
  };

  const fetchInfo = (url) => {
    return fetchWithAuth(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        // Set initial data structure to match what the UI expects
        setData({ data: d || [], total: 0 });
        fetchInfoTotal(`${globalThis.REACT_APP_API_PURE}user/count`, d);
      })
      .catch((error) => {
        logger.error('❌ Error fetching user data:', error);
        // Set empty data on error to prevent React rendering issues
        setData({ data: [], total: 0 });
        notification.error({
          message: 'Error',
          description: 'Failed to load user data. Please try again.',
          placement: 'topRight',
          duration: 3,
        });
      });
  };
  const fetchInfoTotal = (url, datalist) => {
    return fetchWithAuth(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        setData({
          data: datalist || [],
          total: d || 0,
        });
      })
      .catch((error) => {
        logger.error('❌ Error fetching user count:', error);
        // Set total to 0 on error to prevent React rendering issues
        setData({
          data: datalist || [],
          total: 0,
        });
      });
  };

  const fetchDatasetAceess = (url, setdata) =>
    fetchWithAuth(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        const datasetAccess = d?.dataset_access || [];
        // Expand checked items to include all children of checked parents
        const expandedItems = expandCheckedItems(datasetAccess, parent);
        setdata(expandedItems);
      })
      .catch((error) => {
        logger.error('Error fetching dataset access:', error);
        // Set empty array on error to prevent React rendering issues
        setdata([]);
      });

  const handleClickOpen = () => {
    if (rolevert !== '') {
      fetchDatasetAceess(`${globalThis.REACT_APP_API_PURE}user/datavieweraccess?id=${rolevert.id}`, setCheckedItems);
      setInvitation({
        id: rolevert.id,
        email: rolevert.email,
        role: data2.find((item) => item.label === rolevert?.roles?.[0]?.name)?.id || '',
        errorText: '',
      });
    } else {
      setInvitation({ id: '', email: '', role: '', errorText: '' });
    }
    setOpen(true);
  };

  const rejectChangedataset = () => {
    const formData = {
      id: invitation.id,
      action: statelabel.action,
    };
    const apiUrl = `${globalThis.REACT_APP_API_PURE}edit/datasetacess/reject`;
    const headerson = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    });
    const requestOptions = {
      method: 'POST',
      headers: headerson,
      body: JSON.stringify(formData),
    };
    fetch(apiUrl, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data && data.response_code === 200) {
          notification.success({
            message: 'Success',
            description: t('EditDataAccess.1'),
            placement: 'top',
            duration: 5,
          });
          fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
          setOpen(false);
        } else {
          notification.error({
            message: 'Error',
            description: data.error_message,
            placement: 'top',
            duration: 5,
          });
        }
      })
      .catch((error) => logger.error('Error:', error));
  };

  const approveChangedataset = () => {
    const formData = {
      id: invitation.id,
      action: statelabel.action,
    };
    const apiUrl = `${globalThis.REACT_APP_API_PURE}edit/datasetacess/approve`;
    const headerson = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    });
    const requestOptions = {
      method: 'POST',
      headers: headerson,
      body: JSON.stringify(formData),
    };
    fetch(apiUrl, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data && data.response_code === 200) {
          notification.success({
            message: 'Success',
            description: t('Sukses.1'),
            placement: 'top',
            duration: 5,
          });
          fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
          setOpen(false);
        } else {
          notification.error({
            message: 'Error',
            description: data.error_message,
            placement: 'top',
            duration: 5,
          });
        }
      })
      .catch((error) => logger.error('Error:', error));
  };

  const requestChangedataset = (stats) => {
    const sortedResults = rearrangeCheckedResults(checkedItems);
    const filterDataByParentId = (data) => {
      const idSet = new Set(data.map((item, _i) => item.id));
      return data.filter((item) => item.parent_id === '' || idSet.has(item.parent_id));
    };

    function removeCheckIfIndeterminate(data) {
      const idForRemove = [];
      for (const item of data) {
        if (getCheckboxState(item, filterDataByParentId(sortedResults)) === 'indeterminate') {
          idForRemove.push(item.id);
        }
      }
      return removeCheckByIds(filterDataByParentId(sortedResults), idForRemove);
    }

    const formData = {
      id: invitation.id,
      dataset_access: removeCheckIfIndeterminate(parent),
    };
    const apiUrl = `${globalThis.REACT_APP_API_PURE}edit/datasetacess/request`;
    const headerson = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    });
    const requestOptions = {
      method: 'POST',
      headers: headerson,
      body: JSON.stringify(formData),
    };
    fetch(apiUrl, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data && data.response_code === 200) {
          notification.success({
            message: 'Success',
            description: t('RequestDataAccess.1'),
            placement: 'top',
            duration: 5,
          });
          fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
          setOpen(false);
        } else {
          notification.error({
            message: 'Error',
            description: data.error_message,
            placement: 'top',
            duration: 5,
          });
        }
      })
      .catch((error) => logger.error('Error:', error));
  };

  notification.config({
    placement: 'top',
    duration: 5,
  });
  const sendInvitation = (status) => {
    const sortedResults = rearrangeCheckedResults(checkedItems);
    const formData = {
      email: invitation.email,
      role: invitation.label,
      status,
      id: invitation.id,
      dataset_access: sortedResults,
    };
    const apiUrl = `${globalThis.REACT_APP_API_PURE}signup/invitation`;
    const headerson = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    });
    const requestOptions = {
      method: 'POST',
      headers: headerson,
      body: JSON.stringify(formData),
    };
    fetch(apiUrl, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data && data.response_code === 200) {
          if (statelabel.status_pendaftaran === 'Reject') {
            notification.success({
              message: 'Success',
              description: t('UserRejected.1'),
              placement: 'top',
              duration: 5,
            });
          } else {
            notification.success({
              message: 'Success',
              description: t('UserInvited.1'),
              placement: 'top',
              duration: 5,
            });
          }
          fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
          setOpen(false);
        } else {
          alert(data.error_message);
        }
      })
      .catch((error) => logger.error('Error:', error));
  };

  const sendConfirmresetdandelete = (status, action) => {
    const formData = {
      email: invitation.email,
      username: invitation.email,
      role: invitation.role,
      status,
      id: invitation.id,
      action,
    };
    const apiUrl = `${globalThis.REACT_APP_API_PURE}user/resetpassworddelete/confirm`;
    const headerson = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    });
    const requestOptions = {
      method: 'POST',
      headers: headerson,
      body: JSON.stringify(formData),
    };
    fetch(apiUrl, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data && data.response_code === 200) {
          if (statelabel.status_pendaftaran === 'Reject') {
            notification.success({
              message: 'Success',
              description: t('UserRejected.2'),
              placement: 'top',
              duration: 5,
            });
          } else {
            notification.success({
              message: 'Success',
              description: t('DeletePrompt.2'),
              placement: 'top',
              duration: 5,
            });
          }
          fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
          setOpen(false);
        } else {
          notification.error({
            message: 'Error',
            description: data.error_message,
            placement: 'top',
            duration: 5,
          });
        }
      })
      .catch((error) => logger.error('Error:', error));
  };

  const handleSwitchChange = ({ before, after, id }) => {
    setStateSwitching({ statusBefore: before, statusAfter: after, id });
    setisSwitchOpen(true);
  };

  const handleresetChange = () => {
    setisSwitchOpender(true);
    setAnchorEl(false);
  };

  const fetchInfo2 = (url2, role) => {
    return fetchWithAuth(url2, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        setData2(d.data || []);
        return d;
      })
      .catch((error) => {
        logger.error('Error fetching reference list:', error);
        // Set empty array on error to prevent React rendering issues
        setData2([]);
        return { data: [] };
      });
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const handleClosepop = () => {
    setOpen(false);
    setInvitation((current) => ({ ...current, is_edit_dataset: '' }));
  };

  const handleCloseSwitchnModal = () => {
    setisSwitchOpen(false);
  };

  const handleCloseSwitchnModalder = () => {
    setisSwitchOpender(false);
  };

  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  const handleValueChange = (
    label,
    event,
    newValue,
    id,
    idUser,
    status,
    isActive,
    statusKeterangan,
    statusPendaftaran,
    action
  ) => {
    // FIX: This condition now correctly handles both Autocomplete changes AND Approve/Reject clicks.
    // The `newValue.id === '--'` check identifies clicks from the Approve/Reject buttons.
    // Handle case where user has no initial role (id is undefined/null)
    if (newValue && (newValue.label !== id || newValue.id === '--' || id === undefined || id === null)) {
      const statelabelData = {
        status_pendaftaran: statusPendaftaran,
        from: id || 'unassigned',
        to: newValue.label,
        id: idUser,
        status,
        label,
        isactive: isActive,
        status_keterangan: statusKeterangan,
        action,
      };

      setStatelabel(statelabelData);

      if (newValue.id === 'f2714ec9-ab30-4585-bc41-babd5f4af4e4') {
        setCheckedItems([]);
        setDatasetModalOpen(true);
      } else {
        setConfirmationModalOpen(true);
      }
    }
  };

  const handleConfirm = (newValue) => {
    setIsLoading(true);
    if (statelabel.action === 'update dataset access') {
      switch (statelabel.status_pendaftaran) {
        case 'Reject':
          rejectChangedataset();
          break;
        default:
          approveChangedataset();
          break;
      }
    } else if (
      statelabel.status_keterangan === 'Waiting Approval' &&
      (allData?.attributes?.target_role?.[0] === '' || allData?.attributes?.target_role?.[0] === undefined)
    ) {
      if (statelabel.status_pendaftaran === 'Reject') {
        switch (statelabel.action) {
          case 'Create New Account':
            sendInvitation('Reject');
            break;
          default:
            sendConfirmresetdandelete('Reject', statelabel.action);
            break;
        }
      } else {
        switch (statelabel.action) {
          case 'Create New Account':
            sendInvitation('');
            break;
          default:
            sendConfirmresetdandelete('', statelabel.action);
            break;
        }
      }
    } else if (statelabel.action === 'delete') {
      // Handle delete user action
      if (statelabel.status_pendaftaran === 'Reject') {
        // Reject delete request
        const formData = {
          id: statelabel.id,
          action: 'delete',
        };
        const apiUrl = `${globalThis.REACT_APP_API_PURE}user/delete/reject`;
        const headerson = new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        });
        const requestOptions = {
          method: 'POST',
          headers: headerson,
          body: JSON.stringify(formData),
        };

        fetch(apiUrl, requestOptions)
          .then((response) => response.json())
          .then((data) => {
            if (data && data.response_code === 200) {
              notification.success({
                message: 'Success',
                description: 'Delete request rejected successfully',
                placement: 'top',
                duration: 5,
              });
              fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
            } else {
              notification.error({
                message: 'Error',
                description: data.error_message || 'Failed to reject delete request',
                placement: 'top',
                duration: 5,
              });
            }
          })
          .catch((error) => {
            logger.error('Error:', error);
            notification.error({
              message: 'Error',
              description: 'Failed to reject delete request',
              placement: 'top',
              duration: 5,
            });
          });
      } else {
        // Approve delete request
        const formData = {
          id: statelabel.id,
          action: 'delete',
        };
        const apiUrl = `${globalThis.REACT_APP_API_PURE}user/delete/approve`;
        const headerson = new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        });
        const requestOptions = {
          method: 'POST',
          headers: headerson,
          body: JSON.stringify(formData),
        };

        fetch(apiUrl, requestOptions)
          .then((response) => {
            return response.json();
          })
          .then((data) => {
            if (data && data.response_code === 200) {
              notification.success({
                message: 'Success',
                description: 'User deleted successfully',
                placement: 'top',
                duration: 5,
              });
              fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
            } else {
              notification.error({
                message: 'Error',
                description: data.error_message || 'Failed to delete user',
                placement: 'top',
                duration: 5,
              });
            }
          })
          .catch((error) => {
            logger.error('Error:', error);
            notification.error({
              message: 'Error',
              description: 'Failed to delete user',
              placement: 'top',
              duration: 5,
            });
          });
      }
    } else {
      const formData = {
        id: statelabel.id,
        role: statelabel.from === 'unassigned' ? '' : statelabel.from,
        target_role: statelabel.to,
        updated_by: getUser()?.email || getUser(),
        status: statelabel.status,
      };

      const dataViewerRoleLabel = data2.find((role) => role.id === 'f2714ec9-ab30-4585-bc41-babd5f4af4e4')?.label;
      if (statelabel.to === dataViewerRoleLabel) {
        const sortedResults = rearrangeCheckedResults(checkedItems);
        formData.dataset_access = sortedResults;
      }

      const apiUrl = `${globalThis.REACT_APP_API_PURE}user/edit`;
      const headerson = new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      });
      const requestOptions = {
        method: 'POST',
        headers: headerson,
        body: JSON.stringify(formData),
      };

      fetch(apiUrl, requestOptions)
        .then((data) => {
          if (statelabel.status === 'Waiting Approval') {
            notification.success({
              message: 'Success',
              description: t('RolePromp.3', { data: translatedData }),
              placement: 'top',
              duration: 5,
            });
          } else if (statelabel.status_pendaftaran === 'Reject') {
            notification.success({
              message: 'Success',
              description: t('RolePromp.5', { data: translatedData }),
              placement: 'top',
              duration: 5,
            });
          } else {
            notification.success({
              message: 'Success',
              description: t('RolePromp.1', { data: translatedData }),
              placement: 'top',
              duration: 5,
            });
          }
          fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
        })
        .catch((error) => {
          fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
          if (statelabel.status === 'Waiting Approval') {
            notification.error({
              message: 'Error',
              description: t('RolePromp.4', { data: translatedData }),
              placement: 'top',
              duration: 5,
            });
          } else {
            notification.success({
              message: 'Success',
              description: t('RolePromp.2', { data: translatedData }),
              placement: 'top',
              duration: 5,
            });
          }
          logger.error('Error:', error);
        });
    }
    setTimeout(() => {
      setIsLoading(false);
      handleCloseConfirmationModal();
      setDatasetModalOpen(false);
    }, 2000);
  };

  const validateEmail = (email) => {
    if (typeof email !== 'string') return false;

    if (email.length === 0 || email.length > 254) return false;

    const at = email.indexOf('@');
    if (at <= 0 || at !== email.lastIndexOf('@') || at === email.length - 1) return false;

    const local = email.slice(0, at);
    const domain = email.slice(at + 1);

    if (local.length > 64 || domain.length > 253) return false;
    if (domain.includes('..')) return false;

    const isAlphaNum = (c) => (c >= '0' && c <= '9') || (c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z');

    const localAllowed = new Set("!#$%&'*+/=?^_`{|}~-.");

    if (local.startsWith('.') || local.endsWith('.')) return false;
    for (let i = 0; i < local.length; i++) {
      const ch = local[i];
      if (!(isAlphaNum(ch) || localAllowed.has(ch))) return false;
      if (ch === '.' && local[i + 1] === '.') return false;
    }

    const labels = domain.split('.');
    if (labels.length < 2) return false;
    for (const label of labels) {
      if (label.length === 0 || label.length > 63) return false;
      if (label.startsWith('-') || label.endsWith('-')) return false;
      for (const ch of label) {
        if (!(isAlphaNum(ch) || ch === '-')) return false;
      }
    }

    return labels.at(-1).length >= 2;
  };

  const handleConfirmSwitch = (newValue) => {
    setIsLoading(true);
    const formData = {
      id: stateSwitching.id,
      is_active: stateSwitching.statusAfter,
      status: stateSwitching.statusAfter ? 'Active' : 'InActive',
    };
    const apiUrl = `${globalThis.REACT_APP_API_PURE}user/edit`;
    const headerson = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    });
    const requestOptions = {
      method: 'POST',
      headers: headerson,
      body: JSON.stringify(formData),
    };
    fetch(apiUrl, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data && data.response_code === 200) {
          fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${page * rowsPerPage}`);
          notification.success({
            message: 'Success',
            description: `${t('SwitchPromp.1')} ${stateSwitching.statusAfter ? t('Active.1') : t('StatusInactive.1')}`,
            placement: 'top',
            duration: 5,
          });
        }
      })
      .catch((error) => {
        notification.error({
          message: 'Error',
          description: `${t('SwitchPromp.2')} ${stateSwitching.statusAfter ? t('Active.1') : t('StatusInactive.1')}`,
          placement: 'top',
          duration: 0,
        });
        logger.error('Error:', error);
      });
    setTimeout(() => {
      setIsLoading(false);
      handleCloseSwitchnModal();
    }, 2000);
  };

  const handleConfirmReset = () => {
    setIsLoading(true);
    const formData = {
      email: resetpass?.email,
    };
    const apiUrl = `${globalThis.REACT_APP_API_PURE}user/password/reset/request`;
    const headerson = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    });
    const requestOptions = {
      method: 'POST',
      headers: headerson,
      body: JSON.stringify(formData),
    };
    fetch(apiUrl, requestOptions)
      .then((response) => {
        if (!response.ok) {
          throw new Error(t('PromptRequestResetReject.1'));
        } else {
          notification.success({
            message: 'Success',
            description: t('PromptRequestResetSuccess.1'),
            placement: 'top',
            duration: 5,
          });
        }
      })
      .catch((error) => logger.error('Error:', error));
    setTimeout(() => {
      setIsLoading(false);
      handleCloseSwitchnModalder();
    }, 2000);
  };

  const handleCancel = () => {
    handleCloseConfirmationModal();
    setDatasetModalOpen(false);
  };
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));
  const [limit, setLimit] = useState(1);
  const scrollableRef = useRef(null);
  const handleFilterChange = (event) => {
    setFilter(event.target.value);
    setIdAlreadyFetch([]);
    settempdata([]);
    fetchInfrecord(`${globalThis.REACT_APP_API_PURE}datasetrecord?q=${event.target.value}`, setParent);
  };
  const [tempdata, settempdata] = useState([]);
  const fetchInfrecord = (url, setdata) =>
    fetchWithAuth(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((d) => {
        // Handle API response properly - check for error structure
        if (d && typeof d === 'object' && d.code === 401) {
          logger.warn('API returned 401, but token refresh should have handled this');
          setdata([]);
        } else if (d && typeof d === 'object' && Array.isArray(d.data)) {
          // New API structure: { data: [], totalCount: number }
          setdata(d.data || []);
        } else if (Array.isArray(d)) {
          // Legacy API structure: direct array
          setdata(d || []);
        } else {
          setdata([]);
        }
      })
      .catch((error) => {
        logger.error('Error fetching dataset records:', error);
        // Set empty array on error to prevent React rendering issues
        setdata([]);
      });

  const handleScroll = useCallback(() => {
    const element = scrollableRef.current;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 1) {
      inquiryAccountNumber(lastfolderid, limit + 1, { recreate: true, parentCheck: false });
      setLimit(limit + 1);
    }
  }, [tempdata, settempdata]);

  useEffect(() => {
    const element = scrollableRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    // Direct API calls without debouncing for initial load
    // Fetch dataset records
    fetchInfrecord(`${globalThis.REACT_APP_API_PURE}datasetrecord`, setParent);

    // Fetch users
    fetchInfo(`${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${0}`);

    // Fetch reference list
    fetchInfo2(`${globalThis.REACT_APP_API_PURE}reference/list?kode=F004`);
  }, []); // Empty dependency array for initial load only

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [rolevert, setRolevert] = React.useState('');
  const opender = Boolean(anchorEl);
  const handleClickers = (event, index, id) => {
    setAnchorEl(event.currentTarget);
    setResetpass({ email: index });
  };
  const handleClosing = () => {
    setAnchorEl(null);
  };

  const fakeDataUrl = `${globalThis.REACT_APP_API_PURE}user/sessionlist?first=0&max=10000000&type=ALL`;
  const intervalRef = useRef(null);

  const fetchData = useCallback(() => {
    fetch(fakeDataUrl, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((d) => {
        const currentTime = new Date();
        const filteredData = d?.filter((item) => {
          const lastAccessTime = new Date(Number.parseInt(item.lastAccess));
          const timeDifference = (currentTime.getTime() - lastAccessTime.getTime()) / (1000 * 60);
          return timeDifference <= 1;
        });
        setDatas(filteredData);
      });
  }, []);

  useEffect(() => {
    const startPolling = () => {
      clearInterval(intervalRef.current);
      //fetchData();
      //intervalRef.current = setInterval(fetchData, 2000);
    };

    const stopPolling = () => {
      clearInterval(intervalRef.current);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === 'visible') {
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData]);

  const [opendraw, setOpendraw] = useState(false);

  const onClosedraw = () => {
    setOpendraw(false);
  };
  const handleClickbutton = () => {
    setOpendraw(true);
  };
  const filterDuplicates = (datas) => {
    const filteredData = datas.filter(
      (item, index, self) => index === self.findIndex((t) => t.username === item.username)
    );
    return filteredData;
  };
  const getCheckboxState = (node, checkedItems) => {
    if (node.children && node.children.length > 0) {
      const allChildrenChecked = node.children.every((child) => getCheckboxState(child, checkedItems) === 'checked');
      const someChildrenChecked = node.children?.some((child) => getCheckboxState(child, checkedItems) !== 'unchecked');
      if (allChildrenChecked) {
        return 'checked';
      }
      if (someChildrenChecked) {
        return 'indeterminate';
      }
      return 'unchecked';
    }
    return checkedItems?.some((item) => item.id === node.id) ? 'checked' : 'unchecked';
  };

  const filteredData = filterDuplicates(datas);
  const renderTree = (nodes, first, namaNode) => {
    const checkboxState = getCheckboxState(nodes, checkedItems);
    const isParentCheckedEntry = checkedItems?.some((item) => item.id === nodes.id && item.check !== false);
    const isChildCheckedViaParent =
      nodes.type === 'file' &&
      checkedItems?.some((item) => item.id === nodes.idparrent && item.check === true);

    const isChecked =
      checkboxState === 'checked' ||
      isParentCheckedEntry ||
      (isChildCheckedViaParent && checkboxState !== 'unchecked');

    const isIndeterminate =
      checkboxState === 'indeterminate' ||
      checkedItems?.some((item) => item.id === nodes.id && item.check === false && nodes.type === 'folder');

    return (
      <TreeItem
        key={nodes.id}
        nodeId={nodes.id}
        label={
          <Grid container direction={'row'} spacing={1}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(event) => {
                      handleCheck(event, nodes);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    indeterminate={isIndeterminate}
                    icon={<CheckBoxOutlineBlank />}
                    checkedIcon={<CheckBox />}
                    indeterminateIcon={<IndeterminateCheckBoxIcon />}
                  />
                }
                label={nodes.name}
                key={nodes.id}
              />
            </Grid>
          </Grid>
        }
        onClick={async () => {
          const isCheckedNode = checkedItems?.some((item) => {
            const targetId = nodes.id;
            return (
              item.id === targetId &&
              item.check === true &&
              getCheckboxState(nodes, checkedItems) !== 'indeterminate'
            );
          });
          await inquiryAccountNumber(nodes.id, 1, { recreate: false, parentCheck: isCheckedNode });
        }}
      >
        {(() => {
          if (Array.isArray(nodes.children)) {
            return nodes.children.map((node, _i) => renderTree(node, false, nodes.name));
          }
          if (nodes?.data?.recordCount > 0) {
            return <span />;
          }
          return null;
        })()}
      </TreeItem>
    );
  };

  const inquiryAccountNumber = useCallback(
    async (id, limit, { recreate = false, parentCheck = false }) => {
      setIdAlreadyFetch((curr) => [...curr, id]);
      if (idAlreadyFetxh.includes(id) && !recreate) return;

      try {
        const res = await getDataSet(id, getToken(), limit);
        if (res.data.length > 0) {
          const newData = res.data.filter((item) => !tempdata?.some((data) => data.id === item.id));
          const isUsedAsParent = checkedItems?.some((item) => item.parent_id === id);
          if (!isUsedAsParent) {
            handleCheck2(newData, parentCheck, id);
          }
          settempdata((curr) => [
            ...curr,
            ...newData.map((item, _i) => ({
              idparrent: id,
              ...item,
            })),
          ]);
        }
        if (res.data.length > 0) {
          const addChildToNode = (nodes, parentId) => {
            // Check if nodes is valid and iterable
            if (!nodes || !Array.isArray(nodes)) {
              return [];
            }

            const newData = res.data.filter((item) => !tempdata?.some((data) => data.id === item.id));
            return nodes.map((node, _i) => {
              if (node && node.id === parentId) {
                return {
                  ...node,
                  children: newData.map((item, _i) => ({
                    idparrent: id,
                    ...item,
                  })),
                };
              }
              if (node?.children && Array.isArray(node.children) && node.children.length > 0) {
                return {
                  ...node,
                  children: addChildToNode(node?.children, parentId),
                };
              }
              return node;
            });
          };
          setParent((prevData) => addChildToNode(prevData, id));
        }
        return res.data;
      } catch (error) {
        logger.error('Error fetching data:', error);
      }
    },
    [idAlreadyFetxh, checkedItems, tempdata]
  );

  const translatedData = statelabel.to ? t(`roledata.${statelabel.to}`) : null;

  const handleClickInvite = () => {
    setIsLoading(true);
    if (invitation.email !== '' && invitation.role !== '' && validateEmail(invitation.email)) {
      if (invitation.is_edit_dataset != '') {
        requestChangedataset();
      } else {
        sendInvitation('Waiting Approval');
      }
    } else {
      setInvitation({
        ...invitation,
        errorText: validateEmail(invitation.email) ? t('Email and Role is required') : t('Email is not valid'),
      });
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };
  return (
    <>
      <Helmet>
        <title> User Management | COVAS </title>
      </Helmet>
      <Dialog
        fullWidth={true}
        maxWidth={'lg'}
        PaperProps={{ style: { maxHeight: '90vh', minHeight: '20vh' } }}
        open={open}
        onClose={handleClosepop}
      >
        <DialogTitle>{rolevert === '' ? t('InvPeople.1') : 'Edit Dataset Access'}</DialogTitle>
        <DialogContent>
          <Grid container direction={'row'} spacing={2} sx={{ pb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                className="my-3"
                disabled={rolevert != '' && invitation.is_edit_dataset != ''}
                fullWidth
                label="Email"
                id="email"
                type="text"
                value={invitation.email}
                onChange={(event) => setInvitation({ ...invitation, email: event.target.value, errorText: '' })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                disabled={!invitation.email || rolevert != '' || invitation.is_edit_dataset != ''}
                className="my-3"
                id={`combo-box-demo-invite`}
                options={(data2 || []).filter((element) => element.label !== 'super_admin')}
                value={(data2 || []).find((e) => e.id === invitation.role)}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setInvitation({ ...invitation, role: newValue.id, errorText: '', label: newValue.label });
                  } else {
                    setInvitation({ ...invitation, role: '', errorText: '', label: '' });
                  }
                  setCheckedItems([]);
                  setFilter('');
                }}
                renderInput={(params) => <TextField placeholder={t('SelectRole.1')} {...params} />}
              />
            </Grid>

            <Grid item xs={12} container direction={'row'}>
              {invitation.role === 'f2714ec9-ab30-4585-bc41-babd5f4af4e4' && (
                <Grid item xs={12} container direction={'row'}>
                  <Grid item xs={12}>
                    <TextField
                      label="Set Data Record Viewing Permission"
                      variant="outlined"
                      value={filter}
                      size="small"
                      fullWidth
                      onChange={handleFilterChange}
                      style={{ marginBottom: '20px' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TreeView defaultCollapseIcon={<ExpandMoreIcon />} defaultExpandIcon={<ChevronRightIcon />}>
                      {Array.isArray(parent) && parent.length > 0
                        ? parent.map((node, _i) => renderTree(node, true, node.name))
                        : null}
                    </TreeView>
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} container justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                disabled={isLoading}
                style={{
                  backgroundColor: isLoading ? 'gray' : '#12A2CE',
                  color: 'white',
                  minWidth: '120px',
                }}
                onClick={handleClickInvite}
              >
                {isLoading ? 'Loading...' : t('Submit.1')}
              </Button>
            </Grid>
            {invitation.errorText && (
              <Grid item xs={12}>
                <Typography style={{ color: 'red' }}>{invitation.errorText}</Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
      <DialogChangeRole
        isConfirmationModalOpen={isConfirmationModalOpen}
        handleCloseConfirmationModal={handleCloseConfirmationModal}
        statelabel={statelabel}
        t={t}
        handleCancel={handleCancel}
        isLoading={isLoading}
        handleConfirm={handleConfirm}
      />
      <DialogChangeRoleWithDataset
        isDatasetModalOpen={isDatasetModalOpen}
        setDatasetModalOpen={setDatasetModalOpen}
        statelabel={statelabel}
        t={t}
        filter={filter}
        handleFilterChange={handleFilterChange}
        parent={parent}
        renderTree={renderTree}
        isLoading={isLoading}
        handleConfirm={handleConfirm}
      />
      <DialogSwitch
        isSwitchOpen={isSwitchOpen}
        handleCloseSwitchnModal={handleCloseSwitchnModal}
        t={t}
        stateSwitching={stateSwitching}
        isLoading={isLoading}
        handleConfirmSwitch={handleConfirmSwitch}
      />
      <DialogReset
        isSwitchOpender={isSwitchOpender}
        handleCloseSwitchnModalder={handleCloseSwitchnModalder}
        t={t}
        isLoading={isLoading}
        handleConfirmReset={handleConfirmReset}
      />
      <Container>
        <Stack direction="row" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Users
            <div>
              <Typography variant="h5" style={{ color: 'text.disabled' }} gutterBottom>
                {data?.total || 0} {t('Member.1')}
              </Typography>
            </div>
          </Typography>
          <div className="grid justify items-end" style={{ display: 'flex', alignItems: 'center' }}>
            <UserListToolbar numSelected={selected?.length} filterName={filterName} onFilterName={handleFilterByName} />
            <Button
              disabled={!acl.create}
              className="px-6 py-3"
              style={{ backgroundColor: !acl?.create ? 'gray' : '#12A2CE', color: 'white' }}
              variant="contained"
              onClick={() => {
                handleClickOpen();
                setRolevert('');
                setInvitation((current) => ({ ...current, is_edit_dataset: '' }));
              }}
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              {t('AddUser.1')}
            </Button>
          </div>
        </Stack>
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Table>
                <TableHead>
                  <TableRow className="custom-table-row">
                    <StyledTableCell style={{ backgroundColor: '#E2F5FC', color: '#0E7496' }}>
                      {t('Nomor.1')}
                    </StyledTableCell>
                    <StyledTableCell style={{ backgroundColor: '#E2F5FC', color: '#0E7496' }}>Email</StyledTableCell>
                    <StyledTableCell style={{ backgroundColor: '#E2F5FC', color: '#0E7496' }}>
                      {t('StatusActive.1')}
                    </StyledTableCell>
                    <StyledTableCell style={{ backgroundColor: '#E2F5FC', color: '#0E7496' }}>
                      {t('StatusActive.2')}
                    </StyledTableCell>
                    <StyledTableCell style={{ backgroundColor: '#E2F5FC', color: '#0E7496' }}>
                      {t('Role.1')}
                    </StyledTableCell>
                    <StyledTableCell style={{ backgroundColor: '#E2F5FC', color: '#0E7496' }}> </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data && Array.isArray(data?.data) ? (
                    data?.data?.map((index, row) => (
                      <TableRow hover key={`user-${index.id || index.email || row}`}>
                        <TableCell component="th" scope="row" alignItems="center">
                          {page * rowsPerPage + row + 1}
                        </TableCell>
                        <TableCell component="th" scope="row" padding="none">
                          {index.email}
                        </TableCell>
                        <TableCell component="th" scope="row" align="left" padding="none">
                          {(() => {
                            const shouldShowApproval =
                              acl.update &&
                              index?.attributes?.status?.[0] === 'Waiting Approval' &&
                              index?.attributes?.updated_by?.[0] !== username;

                            return shouldShowApproval;
                          })() ? (
                            <Grid container direction="row" spacing={1}>
                              <Grid item xs={6}>
                                <Tooltip
                                  placement="topLeft"
                                  title={(() => {
                                    const action = index?.attributes?.action?.[0];
                                    if (action === 'delete') return 'Reject Delete User';
                                    if (action === 'reset') return 'Reject Reset User Password';
                                    if (action === 'update dataset access') return 'Reject Update Dataset Access';
                                    if (action) return 'Reject Change Role';
                                    return 'Reject Add User';
                                  })()}
                                >
                                  <button
                                    type="button"
                                    aria-label={(() => {
                                      const action = index?.attributes?.action?.[0];
                                      if (action === 'delete') return 'Reject Delete User';
                                      if (action === 'reset') return 'Reject Reset User Password';
                                      if (action === 'update dataset access') return 'Reject Update Dataset Access';
                                      if (action) return 'Reject Change Role';
                                      return 'Reject Add User';
                                    })()}
                                    style={{
                                      color: 'white',
                                      padding: '8px 16px',
                                      borderRadius: 8,
                                      backgroundColor: 'red',
                                      cursor: 'pointer',
                                      width: '100%',
                                      fontSize: '12px',
                                    }}
                                    onClick={() => {
                                      setAllData(index);
                                      setInvitation({ id: index.id, email: index.email });
                                      setStatelabel({ ...statelabel, action: index?.attributes?.action?.[0] });
                                      handleValueChange(
                                        getRejectActionPromptText(index, t),
                                        '',
                                        { label: index?.attributes?.target_role?.[0], id: '--' },
                                        index?.attributes?.target_role?.[0],
                                        index.id,
                                        'Reject',
                                        true,
                                        index?.attributes?.status[0],
                                        'Reject',
                                        index?.attributes?.action?.[0]
                                      );
                                    }}
                                  >
                                    {t('Reject.1')}
                                  </button>
                                </Tooltip>
                              </Grid>
                              <Grid item xs={6}>
                                <Tooltip
                                  placement="topLeft"
                                  title={(() => {
                                    const action = index?.attributes?.action?.[0];
                                    if (action === 'delete') return 'Approve Delete User';
                                    if (action === 'reset') return 'Approve Reset User Password';
                                    if (action === 'update dataset access') return 'Approve Update Dataset Access';
                                    if (action) return 'Approve Change Role';
                                    return 'Approve Add User';
                                  })()}
                                >
                                  <button
                                    type="button"
                                    aria-label={(() => {
                                      const action = index?.attributes?.action?.[0];
                                      if (action === 'delete') return 'Approve Delete User';
                                      if (action === 'reset') return 'Approve Reset User Password';
                                      if (action === 'update dataset access') return 'Approve Update Dataset Access';
                                      if (action) return 'Approve Change Role';
                                      return 'Approve Add User';
                                    })()}
                                    style={{
                                      color: 'white',
                                      padding: '8px 16px',
                                      borderRadius: 8,
                                      backgroundColor: '#12A2CE',
                                      cursor: 'pointer',
                                      border: 'none',
                                      width: '100%',
                                      fontSize: '12px',
                                    }}
                                    onClick={() => {
                                      setAllData(index);
                                      setInvitation({ id: index.id, email: index.email });
                                      setStatelabel({
                                        ...statelabel,
                                        status_pendaftaran: '',
                                        action: index?.attributes?.action?.[0],
                                      });
                                      handleValueChange(
                                        getApproveActionPromptText(index, t),
                                        '',
                                        { label: index?.attributes?.target_role?.[0], id: '--' },
                                        index?.attributes?.target_role?.[0],
                                        index.id,
                                        'Approve',
                                        true,
                                        index?.attributes?.status[0],
                                        '',
                                        index?.attributes?.action?.[0]
                                      );
                                    }}
                                  >
                                    {t('Approve.1')}
                                  </button>
                                </Tooltip>
                              </Grid>
                            </Grid>
                          ) : (
                            <span
                              style={{
                                color: 'white',
                                padding: '10px 20px',
                                borderRadius: 8,
                                backgroundColor: getStatusBackgroundColor(index),
                              }}
                            >
                              {getStatusText(index, t)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            disabled={
                              index?.attributes?.status[0] === 'Waiting Activation' ||
                              index?.attributes?.status[0] === 'Waiting Approval' ||
                              index?.attributes?.status[0] === 'Waiting Reset' ||
                              !acl.update ||
                              index?.roles?.[0]?.name === 'super_admin' ||
                              index?.roles?.[0]?.name === 'Super Admin'
                            }
                            checked={index.enabled}
                            onChange={(event, newValue1) =>
                              handleSwitchChange({ before: index.enabled, after: newValue1, id: index.id })
                            }
                            color="primary"
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                          />
                        </TableCell>
                        <TableCell component="th" scope="row" padding="none">
                          <Autocomplete
                            disabled={
                              index?.attributes?.status[0] === 'Waiting Activation' ||
                              index?.attributes?.status[0] === 'Waiting Approval' ||
                              index?.attributes?.status[0] === 'Waiting Reset' ||
                              !acl.update ||
                              (index?.roles?.[0]?.name === 'super_admin' && index.email === username) ||
                              (index?.roles?.[0]?.name === 'Super Admin' && index.email === username) ||
                              (index?.roles?.[0]?.name === 'super_admin' && index.email !== username) ||
                              (index?.roles?.[0]?.name === 'Super Admin' && index.email !== username)
                            }
                            id={`combo-box-demo-${row}`}
                            options={
                              index?.roles?.[0]?.name === 'super_admin'
                                ? data2
                                : data2?.filter((element) => element.label !== 'super_admin')
                            }
                            sx={{ width: 200 }}
                            value={
                              (data2 || []).find(
                                (e) =>
                                  e?.label ===
                                  (index?.attributes?.status?.[0] === 'Waiting Approval' &&
                                  index?.attributes?.target_role?.[0]
                                    ? index.attributes.target_role[0]
                                    : index?.roles?.[0]?.name)
                              ) || null
                            }
                            onChange={(event, newValue) => {
                              if (newValue) {
                                handleValueChange(
                                  `${t('AreYuSureChangeRole.1')} ${index?.roles?.[0]?.name || 'unassigned'} ${t(
                                    'AreYuSureChangeRole.2'
                                  )} ${newValue.label}`,
                                  event,
                                  newValue,
                                  index?.roles?.[0]?.name,
                                  index.id,
                                  'Waiting Approval',
                                  false,
                                  '',
                                  '',
                                  index?.attributes?.action?.[0]
                                );
                              }
                            }}
                            renderInput={(params) => <TextField {...params} placeholder={index?.roles?.[0]?.name} />}
                          />
                        </TableCell>
                        <TableCell component="th" scope="row" padding="none">
                          <IconButton
                            disabled={
                              index?.attributes?.status[0] === 'Waiting Activation' ||
                              index?.attributes?.status[0] === 'Waiting Approval' ||
                              index?.attributes?.status[0] === 'Waiting Reset' ||
                              !acl.update ||
                              (index?.roles?.[0]?.name === 'super_admin' && index.email === username) ||
                              (index?.roles?.[0]?.name === 'Super Admin' && index.email === username) ||
                              (index?.roles?.[0]?.name === 'super_admin' && index.email !== username)
                            }
                            aria-label="more"
                            id="long-button"
                            aria-controls={opender ? 'long-menu' : undefined}
                            aria-expanded={opender ? 'true' : undefined}
                            aria-haspopup="true"
                            onClick={(event) => {
                              handleClickers(event, index.email);
                              setRolevert(index);
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            id="long-menu"
                            MenuListProps={{ 'aria-labelledby': 'long-button' }}
                            anchorEl={anchorEl}
                            open={opender}
                            onClose={handleClosing}
                          >
                            {rolevert?.roles?.[0]?.name === 'data_viewer' && (
                              <>
                                <MenuItem
                                  onClick={() => {
                                    // Use rolevert to ensure correct user is selected
                                    const selectedUser = rolevert || index;
                                    setAllData(selectedUser);
                                    setInvitation({
                                      id: selectedUser.id,
                                      email: selectedUser.email,
                                      is_edit_dataset: 'true',
                                    });
                                    handleClickOpen();
                                    handleClosing();
                                  }}
                                >
                                  {t('EditAccess.1')}
                                </MenuItem>
                                <Divider sx={{ my: 0.5 }} />
                              </>
                            )}
                            <MenuItem
                              onClick={() => {
                                // Use rolevert instead of index to ensure correct user is selected
                                const selectedUser = rolevert || index;
                                setResetpass({ email: selectedUser.email });
                                setisSwitchOpender(true);
                                setAnchorEl(false);
                                handleClosing();
                              }}
                            >
                              {t('ResetPassword.1')}
                            </MenuItem>
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem
                              onClick={() => {
                                // Use rolevert instead of index to ensure correct user is selected
                                const selectedUser = rolevert || index;
                                setAllData(selectedUser);
                                setInvitation({ id: selectedUser.id, email: selectedUser.email });
                                setStatelabel({
                                  ...statelabel,
                                  action: 'delete',
                                  from: selectedUser?.roles?.[0]?.name || 'unassigned',
                                  to: 'delete',
                                  id: selectedUser.id,
                                  status: 'Waiting Approval',
                                  label: `${t('AreYuSureDelete.1')} ${selectedUser.email}`,
                                  isactive: false,
                                  status_keterangan: '',
                                  status_pendaftaran: '',
                                });
                                // First create the delete request
                                const deleteRequestData = {
                                  id: selectedUser.id,
                                  action: 'delete',
                                  updated_by: getUser()?.email || getUser(),
                                };

                                fetch(`${globalThis.REACT_APP_API_PURE}user/delete/request`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${getToken()}`,
                                  },
                                  body: JSON.stringify(deleteRequestData),
                                })
                                  .then((response) => {
                                    return response.json();
                                  })
                                  .then((data) => {
                                    if (data && data.response_code === 200) {
                                      notification.success({
                                        message: 'Success',
                                        description: 'Delete request submitted successfully. Waiting for approval.',
                                        placement: 'top',
                                        duration: 3,
                                      });
                                      fetchInfo(
                                        `${globalThis.REACT_APP_API_PURE}user?max=${rowsPerPage}&first=${
                                          page * rowsPerPage
                                        }`
                                      );
                                    } else {
                                      notification.error({
                                        message: 'Error',
                                        description: data.error_message || 'Failed to submit delete request',
                                        placement: 'top',
                                        duration: 3,
                                      });
                                    }
                                  })
                                  .catch((error) => {
                                    logger.error('Error submitting delete request:', error);
                                    notification.error({
                                      message: 'Error',
                                      description: 'Failed to submit delete request',
                                      placement: 'top',
                                      duration: 3,
                                    });
                                  });

                                handleClosing();
                              }}
                            >
                              {t('DeleteUser.1')}
                            </MenuItem>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Drawer
            style={{ marginTop: '6.5em', height: '100%', boxShadow: 'none' }}
            title={t('ViewUser.1')}
            onClose={onClosedraw}
            open={opendraw}
            placement="right"
          >
            <List
              size="small"
              itemLayout="horizontal"
              dataSource={filteredData}
              renderItem={(item, index) => (
                <List.Item>
                  <Avatar
                    sizes="small"
                    style={{
                      width: 25,
                      height: 25,
                      backgroundColor: 'green',
                      marginLeft: '0.2em',
                      marginRight: '0.8em',
                    }}
                  />
                  <List.Item.Meta title={item.username} />
                </List.Item>
              )}
            />
          </Drawer>
          <TablePagination
            count={data?.total || 0}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </>
  );
}

const rearrangeCheckedResults = (results) => {
  const map = new Map();
  const rearranged = [];
  const filteredData = results?.filter((item) => item.id) || [];

  for (const item of filteredData) {
    map.set(item.id, item);
  }

  for (const item of filteredData) {
    if (item.parent_id === '' || !map.has(item.parent_id)) {
      rearranged.push(item);
    } else {
      const parent = map.get(item.parent_id);
      if (parent.children) {
        parent.children.push(item);
      } else {
        parent.children = [item];
      }
    }
  }

  const flatten = (items) => {
    const flat = [];
    for (const item of items) {
      flat.push(item);
      if (item.children) {
        flat.push(...flatten(item.children));
        delete item.children;
      }
    }
    return flat;
  };

  const result = flatten(rearranged);
  return result;
};
